import { Link } from 'react-router-dom'

const guide = {
  slug: 'supervised-iphone-explained',
  category: 'holds',
  kicker: 'The mechanism · Supervision',
  title: 'Supervised iPhone, explained: what it is, what it changes, and why it\'s the only block you can\'t undo',
  seoTitle: 'Supervised iPhone Explained: The Only Unremovable Block',
  description: `Supervision is Apple's mode for organisation-owned devices. What a profile can do that Screen Time can't, what it doesn't touch, and how to get it off again.`,
  published: '2026-09-03',
  minutes: 8,
  intro: `Every porn blocker on iPhone lives inside a permission you can take
          back, except one kind. Supervision is the mechanism Apple built so
          that a school or a company can hand out iPhones with settings that
          stick. Used on your own phone, it is the only way to make a filter
          that you, the owner, cannot remove on impulse. This guide explains
          what supervision is in plain terms, what it does and doesn't do to
          your phone, and how it's set up and taken off.`,
  next: ['porn-blocker-you-cant-uninstall', 'lock-screen-time-so-it-cant-be-bypassed', 'how-to-block-porn-on-iphone'],
  body: (
    <>
      <h2>What "supervised" means</h2>
      <p>
        iOS has two modes for a device. An <b>unsupervised</b> iPhone is a
        personal phone: the person holding it is in charge of every setting,
        and anything installed on it, profiles included, can be removed by
        that person. A <b>supervised</b> iPhone is a device that an
        organisation owns and manages: it accepts a set of restrictions that
        Apple reserves for this mode, and it can carry configuration
        profiles marked <b>non-removable</b>.
      </p>
      <p>
        Supervision is set using Apple's own tooling, over a cable, from a
        computer. It's a state of the device, not an app. Nothing about it
        is hidden: <b>Settings → General → About</b> shows "This iPhone is
        supervised", and a line at the top of Settings says so too.
      </p>

      <h2>What a supervised profile can do that Screen Time can't</h2>
      <ul>
        <li><b>Refuse to be removed.</b> On a supervised device, the Remove Profile button can be absent. There's no passcode to guess, because there is no passcode: the profile simply doesn't offer the exit.</li>
        <li><b>Forbid VPN configurations.</b> A VPN app installs but cannot set up its tunnel. This closes the door every DNS filter otherwise leaves open. Detail in <Link to="/guides/porn-blocker-that-blocks-vpn">a porn blocker that blocks VPNs</Link>.</li>
        <li><b>Set the DNS resolver for the whole device</b>, every app and browser, on Wi-Fi and mobile data, with encryption, and keep it set.</li>
        <li><b>Restrict things Screen Time can only limit</b>, such as removing the ability to change certain settings at all rather than putting a passcode in front of them.</li>
      </ul>
      <p>
        Screen Time, by contrast, is a passcode in front of settings you
        still own, and the passcode can be reset with your Apple ID. The
        difference in one line: Screen Time asks you for a code; supervision
        doesn't ask.
        <Link to="/guides/lock-screen-time-so-it-cant-be-bypassed"> How far Screen Time can be locked down</Link>, and where it stops.
      </p>

      <h2>What supervision does not do</h2>
      <p>
        People hear "managed device" and picture a company phone that reads
        your messages. Supervision is a capability, not a behaviour. What a
        supervised iPhone does depends entirely on the profile installed. The
        SELFCNTRL profile does one thing: keep the filter on. Concretely:
      </p>
      <ul>
        <li>No one reads your messages, photos, or history. Filtering is by category at DNS level; there is no history to read.</li>
        <li>No remote wipe, no location, no screenshots, no report to anyone.</li>
        <li>No apps installed for you, no apps hidden, nothing about how the phone works day to day changes.</li>
        <li>Your Apple ID, iCloud, Face ID, payments: untouched.</li>
      </ul>
      <p>
        It's written out in the <Link to="/confidentialite">privacy policy</Link>,
        with the processors we use.
      </p>

      <h2>Does supervising erase the phone?</h2>
      <p>
        Honestly: it can. Apple's standard path to supervision resets the
        device, which is why the SELFCNTRL supervisor starts by having you
        back up (iCloud or the computer) and finishes by restoring. Apps,
        photos, messages and settings come back. Ten minutes, once, and it's
        the only step that needs a computer. Whatever a tool promises about
        "no erase", back up first.
      </p>

      <h2>How the lock is set up</h2>
      <ol>
        <li><Link to="/register">Create your space</Link>. One email address.</li>
        <li>Plug the iPhone or iPad into a Mac or a Windows PC with a cable and run the SELFCNTRL supervisor. It puts the device in supervised mode.</li>
        <li>From your account, scan a QR code with the device's camera. The profile installs and seals itself: filtering DNS by category, VPN configurations forbidden, non-removable.</li>
        <li>Choose your pace (straight cut, or a taper that only climbs) and a night curfew if you want one.</li>
      </ol>
      <p>
        After that there's nothing to open and no password to remember.
        Tightening takes effect at once. Loosening waits twenty-four hours,
        and the floor you set never drops.
      </p>

      <h2>How it comes off</h2>
      <p>
        Not from the phone. From your account, you make a deliberate request
        to release the device; it takes effect after the cooling-off delay,
        and the profile is removed. You remain free to stop. You are no
        longer able to stop in thirty seconds at two in the morning, which is
        the only kind of stopping the whole thing exists to prevent. If you
        ever need to hand the phone to someone else or sell it, the same
        request applies; supervision itself is removed by erasing the device,
        as with any managed iPhone.
      </p>

      <h2>Supervision vs Screen Time vs a DNS profile</h2>
      <table>
        <thead>
          <tr><th></th><th>Screen Time</th><th>DNS profile (unsupervised)</th><th>Supervised profile</th></tr>
        </thead>
        <tbody>
          <tr><td>Who can undo it on the phone</td><td>You, with the passcode or an Apple ID reset</td><td>You, four taps</td><td>Nobody</td></tr>
          <tr><td>Covers every app and browser</td><td>Partly</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>VPN workaround</td><td>Open</td><td>Open</td><td>Closed</td></tr>
          <tr><td>Reads your activity</td><td>No</td><td>Depends on the resolver</td><td>No — category filter, no history</td></tr>
          <tr><td>Needs a computer</td><td>No</td><td>No</td><td>Once, ten minutes</td></tr>
        </tbody>
      </table>

      <h2>Why we put $100 on it</h2>
      <p>
        Because supervision is the one mechanism where the claim "you can't
        remove it" is a property of the operating system, not a marketing
        line. On a device we locked, if you strip the filter off by your own
        means, we send you $100 on top of a full refund.
        <Link to="/remboursement"> Conditions</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'What does "This iPhone is supervised" mean?',
      a: `It means the device is in Apple's managed mode, the one used by
          organisations, and can carry restrictions and non-removable
          profiles that a personal iPhone can't. What it actually restricts
          depends on the profile installed; with SELFCNTRL, only the filter.`
    },
    {
      q: 'Can I remove supervision from my own iPhone?',
      a: `Yes, by erasing the device, or by asking us to release it from
          your account after the cooling-off delay. It's slow on purpose,
          never impossible.`
    },
    {
      q: 'Does supervision let anyone see my screen or my location?',
      a: `Not by itself, and the SELFCNTRL profile doesn't. Supervision is a
          set of permissions; the profile we install uses the ones that keep
          the filter on, and none that look at you.`
    },
    {
      q: 'Will iOS updates remove the profile?',
      a: `No. Supervision and the profile survive iOS updates, restarts and
          SIM changes. A full erase of the device removes them, which is why
          release goes through your account instead.`
    }
  ]
}

export default guide
